import Repository from './Repository';
import { DownloadNutritionLabelForDish } from '@/models/NutritionLabels/DownloadNutritionLabelForDish';
import { DownloadNutritionLabelForRecipe } from '@/models/NutritionLabels/DownloadNutritionLabelForRecipe';
import { PreviewFOPNutritionLabel } from '@/models/NutritionLabels/PreviewFOPNutritionLabel';
import { PreviewNutritionLabel } from '@/models/NutritionLabels/PreviewNutritionLabel';
import { DownloadNutritionLabelContentTypes } from './RequestContentTypes';
import { LabelTypeMetadata } from '@/models/NutritionLabels/LabelTypeMetadata';
import { NutritionLabel } from '@/models/NutritionLabels/NutritionLabel';
import { ListResult } from './ResultTypes/ListResult';
import { AxiosResponse } from 'axios';
import { MethodResult } from './ResultTypes/MethodResult';
import { NutritionCountry } from "@/models/NutritionLabels/NutritionCountry";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {FOPLabelFormatData} from "@/models/NutritionLabels/FOPLabelFormatData";
import { RecipeLabel } from '@/models/NutritionLabels/RecipeLabel';
import {NutritionLabelsList} from "@/models/NutritionLabels/NutritionLabelsList";
import {SaveCustomizedLabelDataDto} from "@/models/NutritionLabels/v2/SaveCustomizedLabelDataDto";
import {LabelConfigurationResponse} from "@/models/NutritionLabels/v2/LabelConfigurationResponse";
import {LabelDownloadRequest} from "@/models/NutritionLabels/v2/LabelDownloadRequest";

export const resource = '/nutritionLabels';
export const v2Url = `${process.env.VUE_APP_BASE_DOMAIN}/api/v2`;

export default {
  downloadNutritionLabelForDish(downloadNutritionLabelForDish: DownloadNutritionLabelForDish, type: number) {
    return Repository.post(`${resource}/DownloadNutritionLabelForDish`, downloadNutritionLabelForDish, { withCredentials: true, headers: { accept: type === 0 ? 'application/pdf' : 'image/jpeg' }, responseType: 'arraybuffer' });
  },
  downloadNutritionLabelForRecipe(
    downloadNutritionLabelForRecipe: DownloadNutritionLabelForRecipe,
    acceptContentType: DownloadNutritionLabelContentTypes,
  ) {
    return Repository.post(
      `${resource}/DownloadNutritionLabelForRecipe`,
      downloadNutritionLabelForRecipe,
      {
        withCredentials: true,
        headers: {
          accept: acceptContentType
        },
        responseType: 'arraybuffer'
      }
    );
  },
  listNutritionCountries() : Promise<AxiosResponse<MethodResult<NutritionCountry[]>>> {
    return Repository.get<MethodResult<NutritionCountry[]>>(`${resource}/ListNutritionCountries`, { withCredentials: true });
  },
  listNutritionLabels(nutritionCountryId: number, roundingRuleIndex: number, labelType: LabelTypeMetadata = LabelTypeMetadata.NutritionFacts) {
    return Repository.post<any, AxiosResponse<ListResult<NutritionLabel>>>(
      `${resource}/ListNutritionLabels`,
      {
        nutritionCountryId,
        ruleIndex: roundingRuleIndex,
        queryOptions: {
          filters: [
            { name: "LabelType", value: labelType }
          ]
        }
      },
      { withCredentials: true });
  },
  previewNutritionLabelForRecipe(previewNutritionLabel: PreviewNutritionLabel) {
    return Repository.post<PreviewNutritionLabel, AxiosResponse<MethodResult<string>>>(
      `${resource}/previewNutritionLabelForRecipe`, previewNutritionLabel, { withCredentials: true }
    );
  },
  previewCautionaryFOPNutritionLabel(labelPreviewData: PreviewFOPNutritionLabel): Promise<AxiosResponse<{ id: number, content: string }>> {
    const key = `${labelPreviewData.labelFormatValue},${labelPreviewData.pdsSizeValue}`;
    const queryParams = `recipeId=${labelPreviewData.recipeId}&labelType=${labelPreviewData.labelType}&metadataSearchKey=${key}`;

    return Repository.get<{ id: number, content: string }>(
      `${resource}/previewFrontOfPackageNutritionLabel?${queryParams}`,
      { withCredentials: true }
    );
  },
  previewFOPNutritionLabel(labelPreviewData: PreviewFOPNutritionLabel): Promise<AxiosResponse<{ id: number, content: string }>> {
    const key = `${labelPreviewData.labelFormatValue},${labelPreviewData.pdsSizeValue},${labelPreviewData.highInMetadata}`;
    const queryParams = `recipeId=${labelPreviewData.recipeId}&labelType=${labelPreviewData.labelType}&metadataSearchKey=${key}`;

    return Repository.get<{ id: number, content: string }>(
      `${resource}/previewFrontOfPackageNutritionLabel?${queryParams}`,
      { withCredentials: true }
    );
  },
  getFreeTextPlaceholdersForNutritionLabel(id: number) {
    return Repository.post(`${resource}/GetFreeTextPlaceholdersForNutritionLabel`, { nutritionLabelId: id }, { withCredentials: true });
  },
  getFrontOfPackageOptions(labelLanguage: LabelLanguage): Promise<AxiosResponse<FOPLabelFormatData[]>> {
    return Repository.get<FOPLabelFormatData[]>(
      `${resource}/GetFrontOfPackageOptions?labelLanguage=${labelLanguage}`,
      { withCredentials: true }
    );
  },
  getCautionaryFrontOfPackageOptions(labelLanguage: LabelLanguage): Promise<AxiosResponse<FOPLabelFormatData[]>> {
    return Repository.get<FOPLabelFormatData[]>(
      `${resource}/GetCautionaryFrontOfPackageOptions?labelLanguage=${labelLanguage}`,
      { withCredentials: true }
    );
  },
  saveRecipeLabel(data: any) {
    return Repository.post(`${resource}/SaveRecipeLabel`, data, { withCredentials: true });
  },
  getRecipeLabel(recipeId: number) {
    return Repository.get<RecipeLabel>(
      `${resource}/GetRecipeLabel?recipeId=${recipeId}`,
      { withCredentials: true }
    );
  },
  listLabelsV2(labelTypeFilter?: LabelTypeMetadata): Promise<AxiosResponse<NutritionLabelsList>> {
    return Repository.get<NutritionLabelsList>(
      `${resource}${labelTypeFilter ? `?labelTypeFilter=${labelTypeFilter}` : ''}`,
      { withCredentials: true, baseURL: v2Url }
    );
  },
  getConfigurationOptionsForLabelV2(countryName: string, templateName: string): Promise<AxiosResponse<LabelConfigurationResponse>> {
    return Repository.get<LabelConfigurationResponse>(
      `${resource}/Configurations?countryName=${countryName}&templateName=${templateName}`,
      { withCredentials: true, baseURL: v2Url }
    );
  },
  getLabelConfigurationV2(recipeId: number): Promise<AxiosResponse<SaveCustomizedLabelDataDto>> {
    return Repository.get<SaveCustomizedLabelDataDto>(
      `${resource}/ForRecipe/${recipeId}`,
      { withCredentials: true, baseURL: v2Url }
    );
  },
  saveLabelConfigurationV2(saveData: SaveCustomizedLabelDataDto): Promise<AxiosResponse<number>> {
    return Repository.post<number>(
      `${resource}/ForRecipe/${saveData.recipeId}/Save`,
      saveData,
      { withCredentials: true, baseURL: v2Url },
    );
  },
  downloadLabelV2(renderData: LabelDownloadRequest): Promise<AxiosResponse<Blob>> {
    return Repository.post<LabelDownloadRequest, AxiosResponse<Blob>>(
      `${resource}/Download`,
      renderData,
      { withCredentials: true, baseURL: v2Url, responseType: 'blob' },
    );
  }
};